import React from "react";
import { Modal, Image, Tag } from "antd";
import { StructuredNote } from "../../lib/format-notes";
import { parseOtcNotes } from "../../lib/otc-request-notes";

type RecordType = "prescription" | "diagnostics";

type Props = {
  record: any | null;
  type: RecordType | null;
  onClose: () => void;
};

function titleCaseStatus(value?: string): string {
  if (!value) return "N/A";
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDateTime(value?: string | Date): string {
  if (!value) return "N/A";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

const CATALOG_SOURCE_LABELS: Record<string, string> = {
  search: "Catalog search",
  write_request: "Custom request",
};

/** Purpose-built display for an OTC catalog order (Prescription.notes
 * prefixed with OTC_REQUEST_JSON:) - replaces the raw JSON blob with an
 * itemized medication list plus delivery details. */
function OtcOrderSummary({ notes }: { notes: string }) {
  const parsed = parseOtcNotes(notes);
  if (!parsed) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h4 className="text-xs font-semibold text-gray-500 uppercase">
          Order Details
        </h4>
        {parsed.catalogSource && (
          <Tag color="blue" className="!m-0">
            {CATALOG_SOURCE_LABELS[parsed.catalogSource] ?? parsed.catalogSource}
          </Tag>
        )}
      </div>

      {parsed.items.length > 0 && (
        <div className="rounded-md border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          {parsed.items.map((item, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between px-3 py-2 bg-white"
            >
              <div className="text-sm text-gray-800">
                {item.name}
                {item.strength && (
                  <span className="text-gray-500"> · {item.strength}</span>
                )}
              </div>
              <div className="text-xs font-medium text-gray-500 whitespace-nowrap">
                Qty {item.qty || 1}
              </div>
            </div>
          ))}
        </div>
      )}

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <Field label="Recipient" value={parsed.recipientDisplay} />
        <Field label="Delivery Type" value={parsed.deliveryType} />
        <Field label="Delivery Address" value={parsed.address} />
        <Field label="Contact" value={parsed.contact} />
      </dl>

      {parsed.additionalNotes && (
        <div>
          <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">
            Additional Notes
          </h5>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">
            {parsed.additionalNotes}
          </p>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) {
  return (
    <div>
      <dt className="text-xs font-semibold text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-700">
        {value || value === 0 ? value : "N/A"}
      </dd>
    </div>
  );
}

export default function MedicalRecordDetailModal({
  record,
  type,
  onClose,
}: Props) {
  if (!record || !type) return null;

  const images =
    type === "prescription"
      ? [record.image, ...(Array.isArray(record.images) ? record.images : [])].filter(
          Boolean
        )
      : [record.image, record.results].filter(Boolean);

  const notes = record.notes || record.comments;
  const otcOrder = typeof notes === "string" ? parseOtcNotes(notes) : null;

  return (
    <Modal
      title={type === "prescription" ? "Prescription Report" : "Diagnostics Report"}
      open={!!record}
      onCancel={onClose}
      footer={null}
      width={560}
    >
      <div className="space-y-4">
        {type === "prescription" ? (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            <Field label="Patient" value={record.name} />
            <Field label="Order ID" value={record.orderId} />
            <Field label="Status" value={titleCaseStatus(record.status)} />
            <Field label="Created At" value={formatDateTime(record.createdAt)} />
            {record.delivery && (
              <Field label="Delivery Method" value={record.delivery} />
            )}
            {record.paymentMethod && (
              <Field label="Payment Method" value={record.paymentMethod} />
            )}
            {record.insurance && (
              <Field label="Insurance" value={record.insurance} />
            )}
          </dl>
        ) : (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            <Field label="Patient" value={record.fullname} />
            <Field label="Test Type" value={record.type} />
            <Field label="Provider" value={record.provider} />
            <Field
              label="Scheduled"
              value={
                record.schedule_date
                  ? `${new Date(record.schedule_date).toLocaleDateString()}${
                      record.schedule_time ? ` · ${record.schedule_time}` : ""
                    }`
                  : "N/A"
              }
            />
            <Field label="Status" value={titleCaseStatus(record.status)} />
            <Field label="Created At" value={formatDateTime(record.createdAt)} />
          </dl>
        )}

        {otcOrder ? (
          <OtcOrderSummary notes={notes} />
        ) : (
          notes && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">
                Notes
              </h4>
              <StructuredNote value={notes} />
            </div>
          )
        )}

        {images.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              Attachments
            </h4>
            {/* antd's Image preview already provides zoom in/out and a close
                icon out of the box - PreviewGroup lets several images share
                one lightbox with next/prev navigation. */}
            <Image.PreviewGroup>
              <div className="flex flex-wrap gap-3">
                {images.map((src: string, idx: number) => (
                  <Image
                    key={idx}
                    src={src}
                    width={96}
                    height={96}
                    className="object-cover rounded border border-gray-200"
                    alt="attachment"
                  />
                ))}
              </div>
            </Image.PreviewGroup>
          </div>
        )}
      </div>
    </Modal>
  );
}
